import React, { useMemo, useState } from 'react'

import {
    Button,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    FormControl,
    Switch,
    TextField,
    Theme,
    Tooltip,
} from '@material-ui/core'
import InfoIcon from '@material-ui/icons/Info'

import { Container, Selector } from '../../../components'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { InspectionType, Schedule } from '../../../models'
import { useDateRange } from '../../../hooks'
import { dateValid } from '../../../helpers'

interface Props {
    theme: Theme
    scheduleList?: Schedule[]
    inspectionTypeList: InspectionType[]
    completeStep: (
        name: string,
        startDate: Date,
        endDate: Date,
        inspectionTypeId: number,
        scheduleId: number,
        damageGoal: number,
        shareWithTenant: boolean,
    ) => void
}

export const InspectionDetailStep = (props: Props) => {
    const { theme, scheduleList, inspectionTypeList, completeStep } = props

    const [doValidate, setDoValidate] = useState(false)
    const [name, setName] = useState('')

    const { dateRange, setStartDate, setEndDate } = useDateRange({
        rangeKey: 'inspectionDetailStepDateRange',
    })

    const [damageGoal, setDamageGoal] = useState(0)
    const [shareWithTenant, setShareWithTenant] = useState(false)

    const [inspectionTypeId, setInspectionTypeId] = useState(-1)
    const [scheduleId, setScheduleId] = useState(UNDEFNIED_SCHEDULE_ID)
    const [userFriendlyScheduleId, setUserFriendlyScheduleId] = useState('')

    const SubHeaderCellStyle: React.CSSProperties = {
        fontWeight: 500,
        fontSize: '15px',
        marginBottom: theme.spacing(2),
    }

    const inputStyle: React.CSSProperties = {
        marginBottom: theme.spacing(4),
    }

    const isValid = () => {
        const nameValid = name !== '' && name.length <= 30
        const inspectionTypeValid = inspectionTypeId !== -1
        const startValid = dateValid(dateRange.startDate)
        const endValid = dateValid(dateRange.endDate)

        return nameValid && inspectionTypeValid && startValid && endValid
    }

    const sortedInspectionTypeList = useMemo(() => {
        return inspectionTypeList.sort((a, b) => a.name.localeCompare(b.name))
    }, [inspectionTypeList])

    return (
        <Container
            direction="column"
            style={{
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <Container
                direction="column"
                scrollY
                style={{
                    flex: 1,
                    minHeight: 0,
                    padding: theme.spacing(2),
                }}
            >
                <Container style={{ ...SubHeaderCellStyle }}>Name:</Container>

                <TextField
                    error={doValidate && (name === '' || name.length > 30)}
                    variant="outlined"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={inputStyle}
                    size="small"
                    helperText={
                        doValidate && name.length > 30
                            ? 'Name must be less than 30 characters'
                            : doValidate && name === ''
                            ? 'Name is required'
                            : ''
                    }
                />

                <Selector
                    size="small"
                    label={'Inspection Type'}
                    error={doValidate && inspectionTypeId === -1}
                    currentValue={inspectionTypeId}
                    customStyle={{
                        formControl: {
                            margin: 0,
                            ...inputStyle,
                        },
                    }}
                    onChange={(
                        event: React.ChangeEvent<{
                            value: unknown
                        }>,
                    ) => {
                        setInspectionTypeId(event.target.value as number)
                    }}
                    data={sortedInspectionTypeList}
                    getDisplayString={(insType) => insType.name}
                    searchable
                />
                {scheduleList && (
                    <Container>
                        <Selector
                            size="small"
                            label={'Schedule'}
                            currentValue={scheduleId}
                            customStyle={{
                                formControl: {
                                    margin: 0,
                                    width: 300,
                                    ...inputStyle,
                                },
                            }}
                            onChange={(
                                event: React.ChangeEvent<{
                                    value: unknown
                                }>,
                            ) => {
                                setScheduleId(event.target.value as number)
                            }}
                            data={[
                                {
                                    id: UNDEFNIED_SCHEDULE_ID,
                                    name: 'None',
                                },
                                ...scheduleList,
                            ]}
                            getDisplayString={(s) => s.name}
                        />
                        <Container
                            style={{
                                marginTop: theme.spacing(2),
                                marginLeft: theme.spacing(1),
                            }}
                        >
                            <Tooltip title="Only select a schedule when wanting to walk vacant units, otherwise select None">
                                <InfoIcon fontSize="small" color="action" />
                            </Tooltip>
                        </Container>
                    </Container>
                )}
                {scheduleId === NEW_SCHEDULE_ID && (
                    <TextField
                        error={
                            doValidate &&
                            (userFriendlyScheduleId === '' ||
                                userFriendlyScheduleId.length > 8)
                        }
                        variant="outlined"
                        value={userFriendlyScheduleId}
                        onChange={(e) =>
                            setUserFriendlyScheduleId(e.target.value)
                        }
                        label="Schedule ID (Required)"
                        style={inputStyle}
                        size="small"
                    />
                )}
                <KeyboardDatePicker
                    error={!dateValid(dateRange.startDate)}
                    value={dateRange.startDate}
                    onChange={(date) => {
                        if (date) {
                            setStartDate(date)
                        }
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    label="Start Date"
                    style={inputStyle}
                    size="small"
                />
                <KeyboardDatePicker
                    error={!dateValid(dateRange.endDate)}
                    value={dateRange.endDate}
                    onChange={(date) => {
                        if (date) {
                            setEndDate(date)
                        }
                    }}
                    format="MM/dd/yyyy"
                    inputVariant="outlined"
                    label="End Date"
                    style={inputStyle}
                    size="small"
                />

                <TextField
                    error={doValidate && damageGoal === 0}
                    variant="outlined"
                    type="text"
                    inputMode="numeric"
                    inputProps={{
                        min: 0,
                        style: { textAlign: 'left' },
                        pattern: '[0-9]*',
                    }}
                    value={String(damageGoal)}
                    onChange={(e) => {
                        const raw = e.target.value.replace(/\D/g, '')
                        // Strip leading zeros: "05" -> "5", "0" or "" -> "0"
                        const trimmed = raw.replace(/^0+/, '') || '0'
                        setDamageGoal(Number(trimmed))
                    }}
                    label="Damage Goal"
                    style={inputStyle}
                    size="small"
                    InputProps={{
                        startAdornment: (
                            <span style={{ marginRight: '8px' }}>$</span>
                        ),
                    }}
                />

                <Container alignItems="center">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={shareWithTenant}
                                color="primary"
                                onChange={() =>
                                    setShareWithTenant(!shareWithTenant)
                                }
                            />
                        }
                        labelPlacement="start"
                        label="Share with tenant"
                        style={{ marginRight: theme.spacing(1) }}
                    />
                    <Tooltip title="Tenants on matching units and areas can see upcoming dates and completed results in the tenant portal">
                        <InfoIcon fontSize="small" color="action" />
                    </Tooltip>
                </Container>
            </Container>

            <Container
                style={{
                    padding: theme.spacing(0, 2, 2, 2),
                    borderTop: `1px solid ${theme.palette.grey[300]}`,
                }}
            >
                <Button
                    onClick={() => {
                        setDoValidate(true)

                        if (isValid()) {
                            completeStep(
                                name,
                                dateRange.startDate!,
                                dateRange.endDate!,
                                inspectionTypeId,
                                scheduleId,
                                damageGoal,
                                shareWithTenant,
                            )
                        }
                    }}
                    variant="outlined"
                    color="primary"
                    fullWidth
                    style={{ textTransform: 'none' }}
                >
                    Create & begin assigning
                </Button>
            </Container>
        </Container>
    )
}

const UNDEFNIED_SCHEDULE_ID = -1
const NEW_SCHEDULE_ID = 0
